import React from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import DeleteForeverIcon from "@material-ui/icons/DeleteForever"
import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import DeleteIcon from '@material-ui/icons/Delete'
import { useStyles } from './Styles'
import PayableAutocomplete from './PayableAutocomplete'
import { PAYABLE_TYPES } from '../../../../../utils/enum'
import { getPriceableType, parseLocaleNumber } from '../../../../../utils/helpers'

const PayablesManager = ({
  payables = [],
  onPayablesChange,
  handleNotificationCall,
  ruleIndex,
  formikErrors,
  formikTouched,
  setFieldTouched
}) => {
  const intl = useIntl()
  const classes = useStyles()

  // Add new payable
  const handleAddPayable = () => {
    const newPayable = {
      payable_type: '',
      payable_id: null,
      payable: null,
      prices: [],
      priceable_type: null,
      priceable_id: null
    }
    onPayablesChange([...payables, newPayable])
  }

  // Remove payable
  const handleRemovePayable = (payableIndex) => {
    const updatedPayables = payables.filter((_, index) => index !== payableIndex)
    onPayablesChange(updatedPayables)
  }

  // Update payable type
  const handlePayableTypeChange = (payableIndex, newType) => {
    const updatedPayables = payables.map((payable, index) =>
      index === payableIndex
        ? {
          ...payable,
          payable_type: newType,
          payable_id: null,
          payable: null,
          prices: [],
          priceable_type: null,
          priceable_id: null
        }
        : payable
    )
    onPayablesChange(updatedPayables)
  }

  // Update payable selection
  const handlePayableSelect = (payableIndex, selectedItem) => {
    const updatedPayables = payables.map((payable, index) =>
      index === payableIndex
        ? {
          ...payable,
          payable_id: selectedItem.id,
          payable: selectedItem,
          prices: selectedItem.prices || [],
          priceable_id: null
        }
        : payable
    )
    onPayablesChange(updatedPayables)
  }

  // Clear payable selection
  const handleClearPayable = (payableIndex) => {
    const updatedPayables = payables.map((payable, index) =>
      index === payableIndex
        ? { ...payable, payable_id: null, payable: null, prices: [], priceable_id: null }
        : payable
    )
    onPayablesChange(updatedPayables)
  }

  // Update priceable_id selection
  const handlePriceableIdChange = (payableIndex, priceableId) => {
    const updatedPayables = payables.map((payable, index) =>
      index === payableIndex
        ? { ...payable, priceable_id: priceableId || null, priceable_type: !!priceableId ? getPriceableType(payable.payable_type) : null }
        : payable
    )
    onPayablesChange(updatedPayables)
  }

  // Handle payable_id field blur
  const handlePayableIdBlur = (payableIndex) => () => {
    if (setFieldTouched) {
      setFieldTouched(`rules[${ruleIndex}].payables[${payableIndex}].payable_id`, true)
    }
  }

  return (
    <div className={classes.payablesManagerWrapper}>
      <Typography variant="h5" className={classes.payablesTitle}>
        <FormattedMessage id='Promocodes.Payables.Title' />
      </Typography>

      {payables && payables.length > 0 ? (
        payables.map((payable, payableIndex) => (
          <Paper key={payableIndex} className={classes.payableCard} elevation={1}>
            <div className={classes.payableCardHeader}>
              <Typography variant="h6" className={classes.payableCardTitle}>
                <FormattedMessage
                  id='Promocodes.Payable.Title'
                  values={{ index: payableIndex + 1 }}
                />
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemovePayable(payableIndex)}
                className={classes.removePayableButton}
              >
                <DeleteIcon />
              </IconButton>
            </div>

            <div className={classes.payableCardContent}>
              {/* Payable Type Select */}
              <div className={classes.BasicInfoRow}>
                <InputLabel id={`payable-type-${ruleIndex}-${payableIndex}`}>
                  <FormattedMessage id='Promocodes.Payable.Type.Label' />
                </InputLabel>
                <FormControl
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  error={
                    formikErrors?.rules?.[ruleIndex]?.payables?.[payableIndex]?.payable_type &&
                    formikTouched?.rules?.[ruleIndex]?.payables?.[payableIndex]?.payable_type
                  }
                >
                  <Select
                    labelId={`payable-type-${ruleIndex}-${payableIndex}`}
                    id={`payable-type-${ruleIndex}-${payableIndex}`}
                    value={payable.payable_type || ''}
                    onChange={(e) => handlePayableTypeChange(payableIndex, e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em><FormattedMessage id='Promocodes.Payable.Type.Placeholder' /></em>
                    </MenuItem>
                    {PAYABLE_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {intl.formatMessage({ id: type.label })}
                      </MenuItem>
                    ))}
                  </Select>
                  {formikErrors?.rules?.[ruleIndex]?.payables?.[payableIndex]?.payable_type &&
                    formikTouched?.rules?.[ruleIndex]?.payables?.[payableIndex]?.payable_type && (
                      <FormHelperText error>
                        {formikErrors.rules[ruleIndex].payables[payableIndex].payable_type}
                      </FormHelperText>
                    )}
                </FormControl>
              </div>

              {/* Payable ID Autocomplete */}
              <div className={classes.BasicInfoRow}>
                <InputLabel id={`payable-id-${ruleIndex}-${payableIndex}`}>
                  <FormattedMessage id='Promocodes.Payable.Id.Label' />
                </InputLabel>
                <div className={classes.payableAutocompleteWrapper}>
                  <PayableAutocomplete
                    payableType={payable.payable_type}
                    selectedPayable={payable.payable}
                    onPayableSelect={(item) => handlePayableSelect(payableIndex, item)}
                    handleNotificationCall={handleNotificationCall}
                    error={formikErrors?.rules?.[ruleIndex]?.payables?.[payableIndex]?.payable_id}
                    touched={formikTouched?.rules?.[ruleIndex]?.payables?.[payableIndex]?.payable_id}
                    onBlur={handlePayableIdBlur(payableIndex)}
                  />
                  {payable.payable && (
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.clearPayableButton}
                      startIcon={<DeleteForeverIcon />}
                      onClick={() => handleClearPayable(payableIndex)}
                    >
                      <FormattedMessage id='Promocodes.Payable.Clear' />
                    </Button>
                  )}
                </div>
              </div>

              {/* Priceable ID Select */}
              {payable.payable && payable.prices && payable.prices.length > 0 && (
                <div className={classes.BasicInfoRow}>
                  <InputLabel id={`priceable-id-${ruleIndex}-${payableIndex}`}>
                    <FormattedMessage id='Promocodes.Payable.Price.Label' />
                  </InputLabel>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  >
                    <Select
                      labelId={`priceable-id-${ruleIndex}-${payableIndex}`}
                      id={`priceable-id-${ruleIndex}-${payableIndex}`}
                      value={payable.priceable_id ?? ""}
                      onChange={(e) => handlePriceableIdChange(payableIndex, e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em><FormattedMessage id='Promocodes.Payable.Price.Placeholder' /></em>
                      </MenuItem>
                      {payable.prices.map((price) => (
                        <MenuItem key={price.id} value={price.id}>
                          <div className={classes.priceChip}>
                            <strong>
                              <span><FormattedNumber value={parseLocaleNumber(price.price)} /></span>
                              <span><FormattedMessage id='egp' /></span>
                            </strong>
                            <FormattedMessage id={`Services.List.Table.Prices.${price.price_duration}`} />
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </div>
          </Paper>
        ))
      ) : (
        <Typography variant="body2" className={classes.noPayablesText}>
          <FormattedMessage id='Promocodes.Payables.NoPayables' />
        </Typography>
      )}

      {/* Add Payable Button */}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleAddPayable}
        className={classes.addPayableButton}
      >
        <FormattedMessage id='Promocodes.Payable.Add' />
      </Button>
    </div>
  )
}

export default PayablesManager

